


























































































































































































































































































































































import { FilialeModel } from "@/models/FilialeModel";
import { ProduktModel } from "@/models/ProduktModel";
import {
   ConvenienceBestandModel,
   ConvenienceBestandResponse,
   FilialBestand,
   SaleRecord,
   DeliveryRecord,
   BewegungRecord,
   DatePaginationMeta,
} from "@/models/ConvenienceBestandModel";
import { PermissionType } from "@/models/UserDataModel";
import Vue from "vue";
import { catVue } from "@/util/logging";

export default Vue.extend({
   name: "ConvenienceAnzeige",

   components: {
      BewegungChart: () =>
         import(
            /* webpackChunkName: "bewegung-chart" */ "@/components/util/BewegungChart.vue"
         ),
   },

   created() {
      catVue.info(`${this.$options.name}:: CREATED... `);
   },

   mounted() {
      catVue.info(
         `${this.$options.name}:: MOUNTED... props.produkt -> ${this.produkt.url}`
      );
      this.loadBestand();
   },

   data: () => ({
      bestandData: null as ConvenienceBestandResponse | null,
      salesData: [] as SaleRecord[],
      deliveryData: [] as DeliveryRecord[],
      bewegungen: [] as BewegungRecord[],
      bewegungenMeta: null as DatePaginationMeta | null,
      isLoading: false,
      isLoadingSales: false,
      isLoadingDeliveries: false,
      isLoadingBewegungen: false,
      errorMessage: "",
      lieferantId: 1,
      gtin: "",
      defaultDateRange: { von: "", bis: "" }, // The initial range from API on first load
      currentDateRange: { von: "", bis: "" }, // The current range we're viewing
      currentPrevLink: "", // The prev link from current range
      currentNextLink: "", // The next link from current range
      suppressAutoLoad: false, // Prevent auto-load when switching filialen
      // Bestand editing state
      isEditingBestand: false,
      editBestandValue: 0,
      editBestandFilialeId: 0,
      isCorrectingBestand: false,
      bestandHeaders: [
         { text: "Filiale", value: "bezeichnung", sortable: true },
         { text: "Kurz", value: "kurz", sortable: true },
         { text: "Bestand", value: "menge", align: "right", sortable: true },
         {
            text: "Letzte Aktivität",
            value: "last_activity",
            sortable: true,
         },
      ],
   }),

   props: {
      produkt: Object as () => ProduktModel,
   },

   computed: {
      filialen(): FilialeModel[] {
         return (this as unknown as { filialenAktiv: FilialeModel[] })
            .filialenAktiv;
      },
      theFiliale(): FilialeModel {
         return (
            this as unknown as {
               $store: { state: { auth: { theFiliale: FilialeModel } } };
            }
         ).$store.state.auth.theFiliale;
      },
      filialenAktiv(): FilialeModel[] {
         return (
            this as unknown as {
               $store: {
                  state: { auth: { filialenAktiv: FilialeModel[] } };
               };
            }
         ).$store.state.auth.filialenAktiv;
      },
      selectedFilialeBestand(): FilialBestand | null {
         if (!this.bestandData || !this.theFiliale) return null;
         return (
            this.bestandData.filialen.find(
               (f) => f.kurz === this.theFiliale.f_kurz
            ) || null
         );
      },
      isAdmin(): boolean {
         const user = (
            this as unknown as {
               $store: {
                  state: { auth: { user: { Permissions: number } | null } };
               };
            }
         ).$store.state.auth.user;
         if (!user) return false;
         return (user.Permissions & PermissionType.PERM_SUPERUSER) !== 0;
      },
   },

   methods: {
      async loadBestand() {
         catVue.info(
            `loadBestand called - produkt: ${
               this.produkt?.titel || "undefined"
            }`
         );
         catVue.info(`produkt.url: ${this.produkt?.url}`);
         catVue.info(`produkt.EAN: ${this.produkt?.EAN}`);
         catVue.info(`produkt.EANliste: ${this.produkt?.EANliste}`);

         if (!this.produkt) {
            catVue.warn("No product provided");
            return;
         }

         // Extract GTIN from product URL: /convenience/1/7350049924441
         let gtin = this.produkt.EAN;
         let lieferantId = 1;

         // The Lieferant is only reliably known via the URL (each GTIN can have
         // multiple cv_lfrnt_artikel rows for different suppliers), so always try
         // to extract it from there, even when EAN is already set.
         if (this.produkt.url) {
            // Try to extract from URL: /convenience/{lieferantId}/{gtin}
            const match = this.produkt.url.match(/\/convenience\/(\d+)\/(\d+)/);
            if (match) {
               lieferantId = parseInt(match[1], 10);
               if (!gtin) gtin = match[2];
               catVue.info(
                  `Extracted from URL - Lieferant: ${lieferantId}, GTIN: ${gtin}`
               );
            }
         }

         if (!gtin) {
            catVue.warn(`No EAN/GTIN found for product: ${this.produkt.titel}`);
            this.errorMessage = "Kein EAN/GTIN für diesen Artikel vorhanden";
            return;
         }

         this.gtin = gtin;
         this.lieferantId = lieferantId;

         catVue.info(
            `Fetching bestand for GTIN: ${gtin}, Lieferant: ${lieferantId}`
         );

         this.isLoading = true;
         this.errorMessage = "";

         try {
            this.bestandData = await ConvenienceBestandModel.getBestand(
               lieferantId,
               gtin
            );
            catVue.info(
               `Loaded bestand for ${gtin}: ${this.bestandData.filialen.length} filialen`
            );

            // Load sales, deliveries, and movements for current filiale
            await Promise.all([
               this.loadSales(),
               this.loadDeliveries(),
               this.loadBewegungen(),
            ]);
         } catch (error) {
            this.errorMessage = `Fehler beim Laden der Bestandsdaten: ${error}`;
            catVue.error(this.errorMessage, error as Error);
         } finally {
            this.isLoading = false;
         }
      },

      async loadSales() {
         if (!this.gtin || !this.theFiliale) {
            catVue.warn("No GTIN or filiale selected");
            return;
         }

         const filialeBestand = this.selectedFilialeBestand;
         if (!filialeBestand) {
            catVue.warn(
               `No bestand data for current filiale: ${this.theFiliale.f_kurz}`
            );
            return;
         }

         this.isLoadingSales = true;

         try {
            const response = await ConvenienceBestandModel.getAbgang(
               this.lieferantId,
               this.gtin,
               filialeBestand.filiale_id,
               3
            );
            this.salesData = response.sales;
            catVue.info(
               `Loaded ${this.salesData.length} sales for filiale ${filialeBestand.filiale_id}`
            );
         } catch (error) {
            catVue.error("Failed to load sales data", error as Error);
            this.salesData = [];
         } finally {
            this.isLoadingSales = false;
         }
      },

      async loadDeliveries() {
         if (!this.gtin || !this.theFiliale) {
            catVue.warn("No GTIN or filiale selected");
            return;
         }

         const filialeBestand = this.selectedFilialeBestand;
         if (!filialeBestand) {
            catVue.warn(
               `No bestand data for current filiale: ${this.theFiliale.f_kurz}`
            );
            return;
         }

         this.isLoadingDeliveries = true;

         try {
            const response = await ConvenienceBestandModel.getZugang(
               this.lieferantId,
               this.gtin,
               filialeBestand.filiale_id,
               3
            );
            this.deliveryData = response.deliveries;
            catVue.info(
               `Loaded ${this.deliveryData.length} deliveries for filiale ${filialeBestand.filiale_id}`
            );
         } catch (error) {
            catVue.error("Failed to load delivery data", error as Error);
            this.deliveryData = [];
         } finally {
            this.isLoadingDeliveries = false;
         }
      },

      async selectFiliale(fil: FilialeModel): Promise<void> {
         catVue.info(`[ConvenienceAnzeige] selectFiliale: ${fil.f_kurz}`);
         this.$store.commit("auth/setFiliale", fil);
         // Suppress auto-load during filiale switch to avoid unexpected navigation
         this.suppressAutoLoad = true;
         // Reset range and links for the new filiale
         this.currentDateRange = { von: "", bis: "" };
         this.currentPrevLink = "";
         this.currentNextLink = "";
         // Reload sales, deliveries, and movements when filiale changes
         await Promise.all([
            this.loadSales(),
            this.loadDeliveries(),
            this.loadBewegungen(),
         ]);
         // Re-enable auto-load after data is loaded
         this.suppressAutoLoad = false;
         catVue.info(
            `[ConvenienceAnzeige] selectFiliale DONE, suppressAutoLoad reset, bewegungen.length=${
               this.bewegungen.length
            }, meta=${!!this.bewegungenMeta}`
         );
      },

      selectFilialeByID(filialeId: number): void {
         // The backend returns filiale._rowid, but we need to find the matching FilialeModel
         // We can't directly match, so just log for now
         catVue.info(`selectFilialeByID: ${filialeId}`);
         // TODO: Match filiale by ID when we have the mapping available
      },

      async resetDateRange(): Promise<void> {
         catVue.info(
            "[ConvenienceAnzeige] resetDateRange: resetting to default"
         );
         this.currentDateRange = { von: "", bis: "" }; // Reset to defaults
         this.currentPrevLink = "";
         this.currentNextLink = "";
         // Reload with default (current week)
         await this.loadBewegungen();
      },

      async loadBewegungen(): Promise<void> {
         catVue.info(
            `[ConvenienceAnzeige] loadBewegungen START, gtin=${this.gtin}, filiale=${this.theFiliale?.f_kurz}`
         );
         if (!this.gtin || !this.theFiliale) {
            catVue.warn("No GTIN or filiale selected");
            return;
         }

         const filialeBestand = this.selectedFilialeBestand;
         if (!filialeBestand) {
            catVue.warn(
               `No bestand data for current filiale: ${this.theFiliale.f_kurz}`
            );
            return;
         }

         catVue.info(
            `[ConvenienceAnzeige] Loading bewegungen for filiale_id=${filialeBestand.filiale_id}`
         );

         // Reset data only on initial load (when currentDateRange is empty)
         const isInitialLoad =
            !this.currentDateRange.von || !this.currentDateRange.bis;
         if (isInitialLoad) {
            this.bewegungen = [];
            this.bewegungenMeta = null;
         }

         this.isLoadingBewegungen = true;
         this.errorMessage = "";

         try {
            // Use current date range if set, otherwise default to last 7 days
            const von = this.currentDateRange.von;
            const bis = this.currentDateRange.bis;
            const response = await ConvenienceBestandModel.getBewegungen(
               this.lieferantId,
               this.gtin,
               filialeBestand.filiale_id,
               von || undefined,
               bis || undefined
            );

            // Only on initial load, store the default range and replace data
            if (isInitialLoad) {
               this.defaultDateRange = {
                  von: response.meta.dateRangeStart,
                  bis: response.meta.dateRangeEnd,
               };
               this.bewegungen = response.bewegungen;
               catVue.info(
                  `[ConvenienceAnzeige] Initial load - stored default range: ${this.defaultDateRange.von} to ${this.defaultDateRange.bis}`
               );
            }

            this.bewegungenMeta = response.meta;

            // Update current range to what we just loaded
            this.currentDateRange = {
               von: response.meta.dateRangeStart,
               bis: response.meta.dateRangeEnd,
            };

            // Store the navigation links for this range
            this.currentPrevLink = response.meta._links.prev || "";
            this.currentNextLink = response.meta._links.next || "";

            catVue.info(
               `[ConvenienceAnzeige] Loaded ${
                  response.bewegungen.length
               } movement records, total=${this.bewegungen.length}, default=[${
                  this.defaultDateRange.von
               },${this.defaultDateRange.bis}], current=[${
                  this.currentDateRange.von
               },${this.currentDateRange.bis}], hasPrev=${!!this
                  .currentPrevLink}, hasNext=${!!this.currentNextLink}`
            );
         } catch (error) {
            catVue.error("Failed to load movement data", error as Error);
            if (isInitialLoad) {
               this.bewegungen = [];
               this.bewegungenMeta = null;
            }
         } finally {
            this.isLoadingBewegungen = false;
         }
      },

      async loadPreviousBewegungen(): Promise<void> {
         catVue.info(
            "[ConvenienceAnzeige] loadPreviousBewegungen START, currentPrevLink=" +
               this.currentPrevLink
         );
         if (!this.currentPrevLink || !this.selectedFilialeBestand) {
            catVue.warn("[ConvenienceAnzeige] No prev link available");
            return;
         }

         this.isLoadingBewegungen = true;
         try {
            // Parse von/bis from the prev link
            const url = new URL(this.currentPrevLink, window.location.origin);
            const von = url.searchParams.get("von");
            const bis = url.searchParams.get("bis");

            catVue.info(
               `[ConvenienceAnzeige] Loading previous: von=${von}, bis=${bis}`
            );

            const response = await ConvenienceBestandModel.getBewegungen(
               this.lieferantId,
               this.gtin,
               this.selectedFilialeBestand.filiale_id,
               von || undefined,
               bis || undefined
            );
            catVue.info(
               `[ConvenienceAnzeige] Loaded ${response.bewegungen.length} previous records`
            );

            // Prepend previous data to existing data
            this.bewegungen = [...response.bewegungen, ...this.bewegungen];

            // IMPORTANT: Don't update currentDateRange to the prev range!
            // We need to track the ENTIRE range we've loaded so far
            // Store the new range as the extended start
            const extendedRange = {
               von: response.meta.dateRangeStart, // This is the earliest date now
               bis: this.currentDateRange.bis, // Keep the existing end
            };
            this.currentDateRange = extendedRange;

            // Update only the prev link (from the response)
            this.currentPrevLink = response.meta._links.prev || "";
            // DON'T update currentNextLink - keep the old one to scroll forward

            catVue.info(
               `[ConvenienceAnzeige] Extended range to: ${
                  this.currentDateRange.von
               } to ${this.currentDateRange.bis}, movements.length=${
                  this.bewegungen.length
               }, hasPrev=${!!this.currentPrevLink}`
            );

            // Update meta with extended range
            if (this.bewegungenMeta) {
               this.bewegungenMeta = {
                  ...response.meta,
                  dateRangeStart: this.currentDateRange.von,
                  dateRangeEnd: this.currentDateRange.bis,
                  _links: {
                     self: this.bewegungenMeta._links.self,
                     prev: this.currentPrevLink,
                     next: this.currentNextLink,
                  },
               };
            }
         } catch (error) {
            catVue.error(
               "Failed to load previous movement data",
               error as Error
            );
         } finally {
            this.isLoadingBewegungen = false;
         }
      },

      async loadNextBewegungen(): Promise<void> {
         catVue.info(
            "[ConvenienceAnzeige] loadNextBewegungen START, currentNextLink=" +
               this.currentNextLink
         );
         if (!this.currentNextLink || !this.selectedFilialeBestand) {
            catVue.warn("[ConvenienceAnzeige] No next link available");
            return;
         }

         this.isLoadingBewegungen = true;
         try {
            // Parse von/bis from the next link
            const url = new URL(this.currentNextLink, window.location.origin);
            const von = url.searchParams.get("von");
            const bis = url.searchParams.get("bis");

            catVue.info(
               `[ConvenienceAnzeige] Loading next: von=${von}, bis=${bis}`
            );

            if (!von || !bis) {
               return;
            }

            const response = await ConvenienceBestandModel.getBewegungen(
               this.lieferantId,
               this.gtin,
               this.selectedFilialeBestand.filiale_id,
               von,
               bis
            );
            catVue.info(
               `[ConvenienceAnzeige] Loaded ${response.bewegungen.length} next records`
            );

            // Append next data to existing data
            this.bewegungen = [...this.bewegungen, ...response.bewegungen];

            // IMPORTANT: Don't update currentDateRange to the next range!
            // We need to track the ENTIRE range we've loaded so far
            // Store the new range as the extended end
            const extendedRange = {
               von: this.currentDateRange.von, // Keep the existing start
               bis: response.meta.dateRangeEnd, // This is the latest date now
            };
            this.currentDateRange = extendedRange;

            // Update only the next link (from the response)
            this.currentNextLink = response.meta._links.next || "";
            // DON'T update currentPrevLink - keep the old one to scroll backward

            catVue.info(
               `[ConvenienceAnzeige] Extended range to: ${
                  this.currentDateRange.von
               } to ${this.currentDateRange.bis}, movements.length=${
                  this.bewegungen.length
               }, hasNext=${!!this.currentNextLink}`
            );

            // Update meta with extended range
            if (this.bewegungenMeta) {
               this.bewegungenMeta = {
                  ...response.meta,
                  dateRangeStart: this.currentDateRange.von,
                  dateRangeEnd: this.currentDateRange.bis,
                  _links: {
                     self: this.bewegungenMeta._links.self,
                     prev: this.currentPrevLink,
                     next: this.currentNextLink,
                  },
               };
            }
         } catch (error) {
            catVue.error("Failed to load next movement data", error as Error);
         } finally {
            this.isLoadingBewegungen = false;
         }
      },

      filClass(fil: FilialeModel): string {
         let ret = fil.css + " fil-" + fil.f_kurz;
         if (this.theFiliale.uid === fil.uid) ret += " fil-selected";
         return ret;
      },

      getFilialBestandByKurz(kurz: string): FilialBestand | null {
         if (!this.bestandData) return null;
         return this.bestandData.filialen.find((f) => f.kurz === kurz) || null;
      },

      getFilialeIdByKurz(kurz: string): number {
         const bestand = this.getFilialBestandByKurz(kurz);
         return bestand ? bestand.filiale_id : 0;
      },

      getBestandForFiliale(fil: FilialeModel): string {
         if (!this.bestandData) return "-";

         // Match by kurz name since we don't have a direct ID mapping
         const bestand = this.bestandData.filialen.find(
            (f) => f.kurz === fil.f_kurz
         );
         return bestand ? String(bestand.menge) : "-";
      },

      bestandClassForFiliale(fil: FilialeModel): string {
         if (!this.bestandData) return "";

         const bestand = this.bestandData.filialen.find(
            (f) => f.kurz === fil.f_kurz
         );
         if (!bestand) return "";

         return this.bestandClass(bestand.menge);
      },

      getLastActivityForFiliale(fil: FilialeModel): string {
         if (!this.bestandData) return "-/-";

         const bestand = this.bestandData.filialen.find(
            (f) => f.kurz === fil.f_kurz
         );
         if (!bestand) return "-/-";

         return this.getLastActivity(bestand);
      },

      getZugangForFiliale(fil: FilialeModel): string | null {
         if (!this.bestandData) return null;

         const bestand = this.bestandData.filialen.find(
            (f) => f.kurz === fil.f_kurz
         );
         if (!bestand || !bestand.last_zugang) return null;

         return this.formatDate(new Date(bestand.last_zugang));
      },

      getAbgangForFiliale(fil: FilialeModel): string | null {
         if (!this.bestandData) return null;

         const bestand = this.bestandData.filialen.find(
            (f) => f.kurz === fil.f_kurz
         );
         if (!bestand || !bestand.last_abgang) return null;

         return this.formatDate(new Date(bestand.last_abgang));
      },

      bestandClass(menge: number): string {
         if (menge < 0) return "text-error";
         if (menge === 0) return "text-warning";
         return "text-success";
      },

      getLastActivity(item: FilialBestand): string {
         const dates = [item.last_zugang, item.last_abgang].filter(
            (d): d is string => d !== null && d !== undefined
         );

         if (dates.length === 0) return "-/-";

         const dateObjects = dates.map((d) => new Date(d));
         const latest = new Date(
            Math.max(...dateObjects.map((d) => d.getTime()))
         );
         return this.formatDate(latest);
      },

      formatDate(date: Date): string {
         const day = String(date.getDate()).padStart(2, "0");
         const month = String(date.getMonth() + 1).padStart(2, "0");
         const year = String(date.getFullYear()).slice(-2);
         return `${day}.${month}.${year}`;
      },

      shouldMaskBestand(menge: number): boolean {
         // Mask bestand < -9 for non-admins
         if (this.isAdmin) return false;
         return menge < -9;
      },

      shouldMaskBestandForFiliale(fil: FilialeModel): boolean {
         if (!this.bestandData) return false;
         const bestand = this.bestandData.filialen.find(
            (f) => f.kurz === fil.f_kurz
         );
         if (!bestand) return false;
         return this.shouldMaskBestand(bestand.menge);
      },

      startEditBestand(filialBestand: FilialBestand | null): void {
         if (!filialBestand) {
            catVue.warn("No filialBestand provided");
            return;
         }
         this.isEditingBestand = true;
         this.editBestandValue = filialBestand.menge;
         this.editBestandFilialeId = filialBestand.filiale_id;
         catVue.info(
            `Started editing bestand for filiale ${filialBestand.filiale_id}: ${filialBestand.menge}`
         );
      },

      cancelEditBestand(): void {
         this.isEditingBestand = false;
         this.editBestandValue = 0;
         this.editBestandFilialeId = 0;
         catVue.info("Cancelled bestand edit");
      },

      async confirmEditBestand(): Promise<void> {
         if (!this.isEditingBestand || this.editBestandFilialeId === 0) {
            return;
         }

         // Validate input
         if (
            this.editBestandValue < 0 ||
            !Number.isInteger(this.editBestandValue)
         ) {
            this.errorMessage = "Bestand muss eine positive Ganzzahl sein";
            return;
         }

         this.isCorrectingBestand = true;
         this.errorMessage = "";

         try {
            const result = await ConvenienceBestandModel.correctBestand(
               this.lieferantId,
               this.gtin,
               this.editBestandFilialeId,
               this.editBestandValue,
               "Manuelle Korrektur durch Admin"
            );

            if (!result.success) {
               this.errorMessage =
                  result.fehler || "Bestandskorrektur fehlgeschlagen";
               return;
            }

            catVue.info(
               `Bestand corrected: ${result.alterBestand} → ${result.neuerBestand} (diff: ${result.differenz})`
            );

            // Update local bestand data
            if (this.bestandData) {
               const filiale = this.bestandData.filialen.find(
                  (f) => f.filiale_id === this.editBestandFilialeId
               );
               if (filiale && result.neuerBestand !== undefined) {
                  filiale.menge = result.neuerBestand;
               }
            }

            // Cancel edit mode
            this.cancelEditBestand();

            // Reload movement data to show the correction
            await this.loadBewegungen();
         } catch (error) {
            catVue.error("Failed to correct bestand", error as Error);
            this.errorMessage = `Fehler beim Korrigieren des Bestands: ${error}`;
         } finally {
            this.isCorrectingBestand = false;
         }
      },
   },

   watch: {
      theFiliale(to: FilialeModel, from: FilialeModel): void {
         catVue.info(
            `${this.$options.name}:: theFiliale '${from.uid}' > '${to.uid}' `
         );
         // Reload deliveries and sales when filiale changes
         if (this.bestandData) {
            Promise.all([this.loadSales(), this.loadDeliveries()]);
         }
      },
      produkt(to: ProduktModel, from: ProduktModel): void {
         catVue.info(
            `${this.$options.name}:: produkt '${from.uid}' > '${to.uid}' `
         );
         this.loadBestand();
      },
   },
});
